import { bootstrapSession } from '@/shared/api/apolloClient';
import { useAuthStore } from '@/stores/authStore';
import { useWorkstationStore } from '@/stores/workstationStore';

const API_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:4000/graphql';
const REST_BASE = API_URL.replace(/\/graphql\/?$/, '');

function authHeaders(): Record<string, string> {
  const token = useAuthStore.getState().access_token;
  const ws = useWorkstationStore.getState().workstation_id;
  const h: Record<string, string> = {};
  if (token) h.Authorization = `Bearer ${token}`;
  if (ws) h['X-Workstation-Id'] = ws;
  return h;
}

/**
 * fetch with the auth headers; on a 401 it runs the same silent refresh the Apollo link
 * uses (bootstrapSession) once and retries with the new token, so REST upload/download
 * survives an expired access token mid-session like every GraphQL call does.
 */
async function authedFetch(url: string, init: RequestInit = {}): Promise<Response> {
  const attempt = (): Promise<Response> =>
    fetch(url, { ...init, credentials: 'include', headers: { ...authHeaders(), ...(init.headers ?? {}) } });
  let res = await attempt();
  if (res.status === 401 && (await bootstrapSession())) {
    res = await attempt();
  }
  return res;
}

/** Streams the file bytes to the REST upload route after the metadata row was registered. */
export async function uploadAttachmentContent(id: string, file: File): Promise<void> {
  const fd = new FormData();
  fd.append('file', file, file.name);
  const res = await authedFetch(`${REST_BASE}/clinical/attachments/${id}/content`, {
    method: 'POST',
    body: fd,
  });
  if (!res.ok) {
    if (res.status === 413) throw new Error('clinical_attachment_too_large');
    let code = 'clinical_attachment_upload_failed';
    try {
      const j = (await res.json()) as { message?: string };
      if (typeof j.message === 'string') code = j.message;
    } catch {
      /* non-JSON error body */
    }
    throw new Error(code);
  }
}

/** Fetches the bytes back and triggers a browser download. */
export async function downloadAttachment(id: string, filename: string): Promise<void> {
  const res = await authedFetch(`${REST_BASE}/clinical/attachments/${id}/content`);
  if (!res.ok) throw new Error('clinical_attachment_download_failed');
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
